import { buildPageableParams, Page, PageableArgs } from '../common';
import { queryUntilLastPage } from '../common/pageable/query-until-last-page';
import {
  EtablissementCreateBodyDto,
  EtablissementDto,
  EtablissementProjection,
  EtablissementUpdateBodyDto,
} from '../etablissement';
import { servicesApi } from '../services';

const ORGANISMES_ETABLISSEMENTS_URL_PREFIX = (id: number) => `organismes/${id}/etablissements`;

const organismeEtablissementApi = servicesApi.injectEndpoints!({
  endpoints: (build) => ({

    addOrganismeEtablissement: build.mutation<void, CreateOrganismeEtablissement, any>({
      query: ({ id, etablissementBody }) => ({
        url: ORGANISMES_ETABLISSEMENTS_URL_PREFIX(id),
        method: 'POST',
        body: etablissementBody,
      }),
      invalidatesTags: ['Etablissements'],
    }),

    getOrganismeEtablissements: build.query<Page<EtablissementDto>, GetOrganismeEtablissementsArgs, any>({
      query: ({ id, ...pageArgs }) => ({
        url: ORGANISMES_ETABLISSEMENTS_URL_PREFIX(id),
        params: buildPageableParams(pageArgs),
      }),
      providesTags: ['Etablissements'],
    }),

    getAllOrganismeEtablissements: build.query<EtablissementDto[], GetAllOrganismeEtablissementsQueryArgs, any>({
      queryFn: ({ id, ...pageArgs }, _api, _extraOptions, baseQuery) => queryUntilLastPage({
        baseQuery,
        url: ORGANISMES_ETABLISSEMENTS_URL_PREFIX(id),
        params: buildPageableParams(pageArgs),
      }),
      providesTags: ['Etablissements'],
    }),

    getAllOrganismeEtablissementsDeclarants: build.query<EtablissementDto[], GetOrganismeEtablissementsDeclarants, any>({
      queryFn: ({ id }, _api, _extraOptions, baseQuery) => queryUntilLastPage({
        baseQuery,
        url: ORGANISMES_ETABLISSEMENTS_URL_PREFIX(id),
        params: buildPageableParams({
          projection: EtablissementProjection.DECLARANT,
          filters: { declarant: true },
        }),
      }),
      providesTags: ['Etablissements'],
    }),

    getOrganismeEtablissement: build.query<EtablissementDto, GetOrganismeEtablissement, any>({
      query: ({ id, etablissementId, projection }) => ({
        url: `${ORGANISMES_ETABLISSEMENTS_URL_PREFIX(id)}/${etablissementId}`,
        params: { projection },
      }),
      providesTags: ['Etablissement'],
    }),

    updateOrganismeEtablissement: build.mutation<void, UpdateOrganismeEtablissement, any>({
      query: ({ id, etablissementId, etablissementBody }) => ({
        url: `${ORGANISMES_ETABLISSEMENTS_URL_PREFIX(id)}/${etablissementId}`,
        method: 'PATCH',
        body: etablissementBody,
      }),
      invalidatesTags: ['Etablissements', 'Etablissement'],
    }),

    deleteOrganismeEtablissement: build.mutation<void, DeleteOrganismeEtablissement, any>({
      query: ({ id, etablissementId }) => ({
        url: `${ORGANISMES_ETABLISSEMENTS_URL_PREFIX(id)}/${etablissementId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Etablissements', 'Etablissement'],
    }),

    getOrganismeEtablissementsConsolidesCount: build.query<number, GetOrganismeEtablissementsConsolidesCountQueryArgs, any>({
      query: ({ id }) => `${ORGANISMES_ETABLISSEMENTS_URL_PREFIX(id)}/count/consolides`,
      providesTags: ['Etablissements'],
    }),
  }),
});

interface GetOrganismeEtablissementsArgs extends PageableArgs<EtablissementDto> {
  id: number;
}

interface GetAllOrganismeEtablissementsQueryArgs extends Omit<PageableArgs<EtablissementProjection>, 'page'> {
  id: number;
}

interface GetOrganismeEtablissementsDeclarants {
  id: number;
}

interface CreateOrganismeEtablissement {
  id: number;
  etablissementBody: EtablissementCreateBodyDto;
}

interface GetOrganismeEtablissement {
  id: number;
  etablissementId: number;
  projection?: EtablissementProjection;
}

interface UpdateOrganismeEtablissement {
  id: number;
  etablissementId: number;
  etablissementBody: EtablissementUpdateBodyDto;
}

interface DeleteOrganismeEtablissement {
  id: number;
  etablissementId: number;
}

interface GetOrganismeEtablissementsConsolidesCountQueryArgs {
  id: number;
}

export const {
  useGetAllOrganismeEtablissementsQuery,
  useAddOrganismeEtablissementMutation,
  useGetOrganismeEtablissementsQuery,
  useGetAllOrganismeEtablissementsDeclarantsQuery,
  useGetOrganismeEtablissementQuery,
  useLazyGetOrganismeEtablissementQuery,
  useUpdateOrganismeEtablissementMutation,
  useDeleteOrganismeEtablissementMutation,
  useGetOrganismeEtablissementsConsolidesCountQuery,
} = organismeEtablissementApi;